import { useState } from "react";
import {
  ArrowDownLeft,
  ArrowUpRight,
  BookOpen,
  KeyRound,
  Plug,
  ShieldCheck,
  Sparkles,
  Wrench,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useWebhooks } from "@/store/webhookStore";
import { APPS } from "@/data/apps";
import { Modal } from "@/components/ui/Modal";
import { CodeBlock } from "./webhookUi";

const accent = APPS.integrations.accent;

/**
 * The in-app tutorial for the whole integration surface. Mirrors
 * docs/INTEGRATIONS.md — update both when behaviour changes.
 */

const CURL_EXAMPLE = `curl -X POST 'https://<your-hive>/hooks/in/wh_…/whsec_…' \\
  -H 'Content-Type: application/json' \\
  -d '{"event":"deploy.finished","service":"api","ok":true}'`;

const VERIFY_EXAMPLE = `// Verify X-Hive-Signature on your endpoint (Node)
const expected = "v1=" + crypto
  .createHmac("sha256", process.env.HIVE_WEBHOOK_SECRET)
  .update(\`\${req.headers["x-hive-timestamp"]}.\${rawBody}\`)
  .digest("hex");
const ok = crypto.timingSafeEqual(
  Buffer.from(req.headers["x-hive-signature"]),
  Buffer.from(expected));`;

const GITHUB_STEPS = `GitHub → repo → Settings → Webhooks → Add webhook
  Payload URL   the Hive endpoint URL you copied
  Content type  application/json
  Secret        the whsec_… signing secret (Hive verifies each request)
  Events        pick what you need (pushes, PRs, issues…)`;

type SectionId = "overview" | "connect" | "errors" | "inbound" | "outbound" | "power";

const SECTIONS: { id: SectionId; label: string; icon: LucideIcon }[] = [
  { id: "overview", label: "How it fits together", icon: BookOpen },
  { id: "connect", label: "Connect an app", icon: Plug },
  { id: "errors", label: "Read & fix errors", icon: Wrench },
  { id: "inbound", label: "Inbound webhooks", icon: ArrowDownLeft },
  { id: "outbound", label: "Outbound webhooks", icon: ArrowUpRight },
  { id: "power", label: "Power-user tips", icon: Sparkles },
];

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2.5">
      <span
        className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full text-[10px] font-bold text-black"
        style={{ background: accent }}
      >
        {n}
      </span>
      <div className="min-w-0">
        <div className="text-xs font-semibold text-app">{title}</div>
        <div className="mt-0.5 text-[11px] leading-relaxed text-app-2">{children}</div>
      </div>
    </div>
  );
}

function HealthRow({ state, meaning, fix }: { state: string; meaning: string; fix: string }) {
  return (
    <div className="rounded-lg border border-app bg-surface-2/30 p-2.5 text-[11px]">
      <div className="font-semibold text-app">{state}</div>
      <div className="mt-0.5 leading-relaxed text-app-2">{meaning}</div>
      <div className="mt-1 leading-relaxed text-emerald-500">→ {fix}</div>
    </div>
  );
}

export function IntegrationGuideModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [section, setSection] = useState<SectionId>("overview");
  const eventDefs = useWebhooks((s) => s.eventDefs);

  return (
    <Modal
      open={open}
      onClose={onClose}
      accent={accent}
      icon={<BookOpen size={18} />}
      title="Integration guide"
      subtitle="Connecting apps, reading errors, and wiring webhooks — the full tour"
      width={860}
    >
      <div className="flex gap-4" style={{ minHeight: 420 }}>
        {/* nav */}
        <div className="w-44 shrink-0 space-y-1">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              onClick={() => setSection(s.id)}
              className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs font-medium transition"
              style={{
                background: section === s.id ? `${accent}1f` : "transparent",
                color: section === s.id ? accent : "var(--text-2)",
              }}
            >
              <s.icon size={13} /> {s.label}
            </button>
          ))}
        </div>

        {/* content */}
        <div className="min-w-0 flex-1 space-y-4 overflow-y-auto pr-1 no-scrollbar" style={{ maxHeight: "58vh" }}>
          {section === "overview" && (
            <>
              <p className="text-xs leading-relaxed text-app-1">
                Hive talks to the outside world in three ways. Each shows up in a different place in the
                Integration Center:
              </p>
              <div className="space-y-2">
                <div className="rounded-xl border border-app bg-surface-2/30 p-3">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-app"><Plug size={13} style={{ color: accent }} /> Connectors</div>
                  <p className="mt-1 text-[11px] leading-relaxed text-app-2">
                    Installed from the catalogue (130+ providers). Hive holds a credential and calls the
                    provider's API — most connectors verify the credential <em>live</em> against the real API the
                    moment you paste it.
                  </p>
                </div>
                <div className="rounded-xl border border-app bg-surface-2/30 p-3">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-app"><ArrowDownLeft size={13} className="text-emerald-500" /> Inbound webhooks</div>
                  <p className="mt-1 text-[11px] leading-relaxed text-app-2">
                    Hive hosts a URL; external services push events to it (GitHub pushes, Stripe payments,
                    alerts). This is how most real-world integrations deliver data — no polling.
                  </p>
                </div>
                <div className="rounded-xl border border-app bg-surface-2/30 p-3">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-app"><ArrowUpRight size={13} className="text-sky-400" /> Outbound webhooks</div>
                  <p className="mt-1 text-[11px] leading-relaxed text-app-2">
                    Hive pushes signed events (task created, integration failed, workspace verified…) to
                    <em> your</em> endpoints — feed dashboards, Zapier, incident bots, anything with a URL.
                  </p>
                </div>
              </div>
              <p className="text-[11px] leading-relaxed text-app-2">
                Everything is tenant-scoped and audited. Webhooks come in three levels: <b>personal</b> (yours),
                <b> workspace</b> (shared with the team in that workspace) and <b>org</b> (admin-managed).
              </p>
            </>
          )}

          {section === "connect" && (
            <>
              <div className="space-y-3">
                <Step n={1} title="Open the catalogue">
                  Hit <b>Connect App</b> in the Integration Center header, search for the provider, and pick it.
                  Cards marked <b>Live test</b> verify credentials against the provider's real API.
                </Step>
                <Step n={2} title="Get a credential from the provider">
                  The connect form tells you which kind it needs — <b>API key</b> (provider dashboard → API keys),
                  <b> access token</b> (developer settings → personal access tokens), or <b>OAuth</b> (until an
                  OAuth app is registered for your org, paste a personal access token instead). Each form links
                  the provider's API documentation.
                </Step>
                <Step n={3} title="Paste it — Hive verifies live">
                  On submit Hive calls the provider (e.g. GitHub's <code className="font-mono text-[10px]">/user</code>) with your
                  credential. Success connects instantly; a rejection keeps the form open with the provider's
                  answer so you can fix the credential on the spot. Credentials are stored server-side, never
                  shown again, and never sent anywhere except the provider's own API.
                </Step>
                <Step n={4} title="No credential yet? Install only">
                  <b>Install only</b> parks the connector as <em>Pending</em> — wire the credential later from the
                  card's Connect button.
                </Step>
                <Step n={5} title="Webhook-style providers">
                  Providers like Zapier, Make or Prometheus connect over webhooks instead of credentials — install
                  them, then create an <b>inbound webhook</b> (Webhooks tab) and link it to the integration.
                </Step>
              </div>
            </>
          )}

          {section === "errors" && (
            <>
              <p className="text-xs leading-relaxed text-app-1">
                Failures are sticky and visible: the card shows a red health dot, the detail panel shows the
                exact provider answer in a banner, and the banner offers the fix. A successful test clears it.
              </p>
              <div className="grid grid-cols-1 gap-2">
                <HealthRow
                  state="Reauth required / credential rejected"
                  meaning="The provider answered 401/403 — the token expired, was revoked, or lacks scopes."
                  fix="Update credential in the error banner (or Reauthorize) with a fresh token, then Test."
                />
                <HealthRow
                  state="Permission missing / pending"
                  meaning="Installed without a credential, or the token lacks a scope the connector needs."
                  fix="Open Connect, paste a credential with the scopes listed on the card."
                />
                <HealthRow
                  state="Provider unreachable"
                  meaning="Hive couldn't reach the provider (network, DNS, outage). The credential was kept."
                  fix="Re-run Test when the provider is back — the banner shows the last attempt time."
                />
                <HealthRow
                  state="Webhook failed"
                  meaning="An endpoint linked to this integration is rejecting deliveries."
                  fix="Open the Webhooks tab, check the delivery log's response body, fix the sender/receiver, redeliver."
                />
              </div>
              <p className="text-[11px] leading-relaxed text-app-2">
                <b>Test</b> runs a real call using the stored credential any time. <b>Test all</b> (header button)
                sweeps every connected integration and reports which ones need attention.
              </p>
            </>
          )}

          {section === "inbound" && (
            <>
              <div className="space-y-3">
                <Step n={1} title="Create the endpoint">
                  Webhooks tab → <b>New webhook</b> → <b>Inbound</b>. Pick the level (personal / workspace / org)
                  and optionally link an integration — its card lights up when events arrive.
                </Step>
                <Step n={2} title="Copy the URL and secret (shown once)">
                  The URL embeds a token — treat it as a credential. The <code className="font-mono text-[10px]">whsec_…</code> secret
                  doubles as the HMAC key if the sender supports signing. Both can be re-revealed or rotated later
                  (rotating invalidates the old URL immediately).
                </Step>
                <Step n={3} title="Paste it into the provider">
                  <CodeBlock text={GITHUB_STEPS} />
                </Step>
                <Step n={4} title="Or push events from anything">
                  <CodeBlock text={CURL_EXAMPLE} />
                </Step>
                <Step n={5} title="Watch the delivery log">
                  Every request — accepted or rejected — lands in the webhook's delivery log with payload, source
                  IP and the rejection reason (bad token, failed signature), so a misconfigured sender is
                  diagnosable in one glance. Accepted events also appear in the Activity Center.
                </Step>
              </div>
              <p className="text-[11px] leading-relaxed text-app-2">
                Handshakes handled for you: Slack's <code className="font-mono text-[10px]">url_verification</code> challenge and
                Dropbox-style GET <code className="font-mono text-[10px]">?challenge=</code> echoes. GitHub's{" "}
                <code className="font-mono text-[10px]">X-Hub-Signature-256</code> and Hive-style{" "}
                <code className="font-mono text-[10px]">X-Hive-Signature</code> HMACs are verified when present.
              </p>
            </>
          )}

          {section === "outbound" && (
            <>
              <div className="space-y-3">
                <Step n={1} title="Create the subscription">
                  Webhooks tab → <b>New webhook</b> → <b>Outbound</b>. Point it at your HTTPS endpoint and choose
                  events — or <b>*</b> for everything.
                </Step>
                <Step n={2} title="Verify the signature on your side">
                  Every delivery carries <code className="font-mono text-[10px]">X-Hive-Event</code>,{" "}
                  <code className="font-mono text-[10px]">X-Hive-Delivery</code>,{" "}
                  <code className="font-mono text-[10px]">X-Hive-Timestamp</code> and{" "}
                  <code className="font-mono text-[10px]">X-Hive-Signature</code>:
                  <div className="mt-2">
                    <CodeBlock text={VERIFY_EXAMPLE} />
                  </div>
                </Step>
                <Step n={3} title="Failures retry, then pause themselves">
                  Non-2xx and network errors retry automatically (30s, then 2m). After 10 consecutive failures the
                  webhook auto-disables — the detail panel explains why and offers one-click re-enable. Any past
                  delivery can be <b>redelivered</b> from the log.
                </Step>
              </div>
              <div>
                <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-app-2">
                  Event catalog {eventDefs.length > 0 && `(${eventDefs.length})`}
                </div>
                {eventDefs.length === 0 ? (
                  <p className="text-[11px] text-app-2">Open the Webhooks tab once to load the live catalog.</p>
                ) : (
                  <div className="space-y-1">
                    {eventDefs.map((e) => (
                      <div key={e.event} className="flex items-baseline gap-2 text-[11px]">
                        <code className="shrink-0 rounded bg-surface-2 px-1.5 py-0.5 font-mono text-[10px]" style={{ color: accent }}>
                          {e.event}
                        </code>
                        <span className="leading-relaxed text-app-2">{e.description}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {section === "power" && (
            <>
              <div className="space-y-2">
                {[
                  { icon: ShieldCheck, title: "Levels & RBAC", text: "Personal webhooks are managed by their creator, workspace (team) webhooks by any member, org webhooks by admins. Admins see everything; viewers see but can't touch." },
                  { icon: KeyRound, title: "Secret hygiene", text: "Rotate a webhook secret any time — the old URL/signature dies instantly, the new one is shown once. Every reveal and rotation is written to the audit log." },
                  { icon: Wrench, title: "Debug from the log", text: "Delivery rows keep the payload, response body, latency and attempt count. Expand a row → Redeliver replays the exact payload after you fix your endpoint." },
                  { icon: Plug, title: "Wildcard subscriptions", text: "Events support exact names, prefixes (task.*) and * for everything — start wide, narrow once you know what you need." },
                  { icon: Sparkles, title: "Automate Hive itself", text: "The same APIs the UI uses are yours: /api/openapi.json describes them. Create webhooks, install connectors and run tests from CI with a session or bearer token." },
                ].map((tip) => (
                  <div key={tip.title} className="flex gap-2.5 rounded-xl border border-app bg-surface-2/30 p-3">
                    <tip.icon size={15} className="mt-0.5 shrink-0" style={{ color: accent }} />
                    <div>
                      <div className="text-xs font-semibold text-app">{tip.title}</div>
                      <p className="mt-0.5 text-[11px] leading-relaxed text-app-2">{tip.text}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] leading-relaxed text-app-2">
                The full written version of this guide lives in the repo at <code className="font-mono text-[10px]">docs/INTEGRATIONS.md</code>.
              </p>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
